import React, { useEffect, useState } from "react";
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import clsx from "clsx";
import { CalendarIcon, ListBulletIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import { type MyLatestReminderList } from "./matterId/types";
import EventTable from "./matterId/_components/eventTable";
import { remindersAndEvents } from "./mocks/calendarMocks";
import CalendarContainer from "./matterId/_components/calendarContainer";
import EventTableTeam from "./matterId/_components/eventTableTeam";
import TeamCalendarContainer from "./matterId/_components/teamCalendarContainer";
import { useSearchParams } from "react-router";
import { Button } from "@/components/feature/ui/button/button";
import GanttChart from "@/views/home/calendars/GanttChart";

const CreateIcon: React.FC = () => <span className="mr-2">+</span>;

export const CalendarView = () => {
  const [searchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState(0);
  const [, setSelectedDate] = useState<Date | null>(null);
  const [eventsForSelectedDate, setEventsForSelectedDate] = useState<MyLatestReminderList["reminderList"]>([]);

  const handleSelectDate = (date: Date | null, events: MyLatestReminderList["reminderList"]) => {
    setSelectedDate(date);
    setEventsForSelectedDate(events);
  };

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && !isNaN(Number(tab))) {
      setSelectedTab(Number(tab));
    }
  }, [searchParams]);

  const sortedEvents = remindersAndEvents
    .sort((a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime())
    .slice(0, 8);

  return (
    <div className="flex h-screen flex-col">
      <div className="flex items-center justify-between py-4">
        <div className="flex items-center">
          <div className="flex space-x-4">
            <Button onClick={() => console.log("Create clicked")} variant="primary">
              <CreateIcon />
              Create
            </Button>
          </div>
        </div>
      </div>
      <TabGroup
        selectedIndex={selectedTab}
        onChange={index => {
          setSelectedTab(index);
          setSelectedDate(null);
          setEventsForSelectedDate([]);
        }}
      >
        <TabList className="flex space-x-8 border-b border-gray-200" aria-label="Tabs">
          <Tab
            className={({ selected }) =>
              clsx(
                "group inline-flex items-center border-b-2 px-1 py-4 text-sm font-medium focus:outline-none",
                selected
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
              )
            }
          >
            {({ selected }) => (
              <>
                <ListBulletIcon
                  className={clsx(
                    "-ml-0.5 mr-2 h-5 w-5",
                    selected ? "text-indigo-500" : "text-gray-400 group-hover:text-gray-500",
                  )}
                  aria-hidden="true"
                />
                <span>My List</span>
              </>
            )}
          </Tab>
          <Tab
            className={({ selected }) =>
              clsx(
                "group inline-flex items-center border-b-2 px-1 py-4 text-sm font-medium focus:outline-none",
                selected
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
              )
            }
          >
            {({ selected }) => (
              <>
                <CalendarIcon
                  className={clsx(
                    "-ml-0.5 mr-2 h-5 w-5",
                    selected ? "text-indigo-500" : "text-gray-400 group-hover:text-gray-500",
                  )}
                  aria-hidden="true"
                />
                <span>My Calendar</span>
              </>
            )}
          </Tab>
          <Tab
            className={({ selected }) =>
              clsx(
                "group inline-flex items-center border-b-2 px-1 py-4 text-sm font-medium focus:outline-none",
                selected
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
              )
            }
          >
            {({ selected }) => (
              <>
                <UserGroupIcon
                  className={clsx(
                    "-ml-0.5 mr-2 h-5 w-5",
                    selected ? "text-indigo-500" : "text-gray-400 group-hover:text-gray-500",
                  )}
                  aria-hidden="true"
                />
                <span>Team List</span>
              </>
            )}
          </Tab>
          <Tab
            className={({ selected }) =>
              clsx(
                "group inline-flex items-center border-b-2 px-1 py-4 text-sm font-medium focus:outline-none",
                selected
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
              )
            }
          >
            {({ selected }) => (
              <>
                <CalendarIcon
                  className={clsx(
                    "-ml-0.5 mr-2 h-5 w-5",
                    selected ? "text-indigo-500" : "text-gray-400 group-hover:text-gray-500",
                  )}
                  aria-hidden="true"
                />
                <span>Team Calendar</span>
              </>
            )}
          </Tab>
          <Tab
            className={({ selected }) =>
              clsx(
                "group inline-flex items-center border-b-2 px-1 py-4 text-sm font-medium focus:outline-none",
                selected
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
              )
            }
          >
            {({ selected }) => (
              <>
                <CalendarIcon
                  className={clsx(
                    "-ml-0.5 mr-2 h-5 w-5",
                    selected ? "text-indigo-500" : "text-gray-400 group-hover:text-gray-500",
                  )}
                  aria-hidden="true"
                />
                <span>Gantt Chart</span>
              </>
            )}
          </Tab>
        </TabList>
        <TabPanels className="mt-5">
          <TabPanel className="rounded-xl bg-white p-5 shadow-md">
            <h3 className="mb-5 text-2xl font-semibold">My List</h3>
            <EventTable
              eventsAndReminders={sortedEvents}
              onAddSubtask={() => console.log("Add Subtask")}
              onSetReminder={() => console.log("Set Reminder")}
              onEditEvent={() => console.log("Edit Event")}
              onDeleteEvent={() => console.log("Delete Event")}
            />
          </TabPanel>
          <TabPanel className="rounded-xl bg-white p-5 shadow-md">
            <h3 className="mb-5 text-2xl font-semibold">My Calendar</h3>
            <CalendarContainer onSelectDate={handleSelectDate} />
            <EventTable
              eventsAndReminders={eventsForSelectedDate}
              onAddSubtask={() => console.log("Add subtask")}
              onSetReminder={() => console.log("Set reminder")}
              onEditEvent={() => console.log("Edit event")}
              onDeleteEvent={() => console.log("Delete event")}
            />
          </TabPanel>

          <TabPanel className="rounded-xl bg-white p-5 shadow-md">
            <h3 className="mb-5 text-2xl font-semibold">Team List</h3>
            <EventTableTeam
              eventsAndReminders={remindersAndEvents}
              onAddSubtask={() => console.log("Add subtask")}
              onSetReminder={() => console.log("Set reminder")}
              onEditEvent={() => console.log("Edit event")}
              onDeleteEvent={() => console.log("Delete event")}
            />
          </TabPanel>
          <TabPanel className="rounded-xl bg-white p-5 shadow-md">
            <h3 className="mb-5 text-2xl font-semibold">Team Calendar</h3>
            <TeamCalendarContainer
              eventsAndReminders={remindersAndEvents}
              onAddSubtask={() => console.log("Add subtask")}
              onSetReminder={() => console.log("Set reminder")}
              onEditEvent={() => console.log("Edit event")}
              onDeleteEvent={() => console.log("Delete event")}
              onSelectDate={handleSelectDate}
            />
            <EventTable
              eventsAndReminders={eventsForSelectedDate}
              onAddSubtask={() => console.log("Add subtask")}
              onSetReminder={() => console.log("Set reminder")}
              onEditEvent={() => console.log("Edit event")}
              onDeleteEvent={() => console.log("Delete event")}
            />
          </TabPanel>
          <TabPanel className="rounded-xl bg-white p-5 shadow-md">
            <h3 className="mb-5 text-2xl font-semibold">Gantt</h3>
            <GanttChart />
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </div>
  );
};
